// Reglas de ubicación de stock compartidas. Un dato, un helper: `resolve_principal_location_id`
// en el backend usa el mismo criterio (principal, o si no la primera) — que el
// desplegable del alta preseleccione otra cosa dejaría al usuario eligiendo "a ciegas"
// entre dos destinos que no coinciden.

/**
 * Ubicación por defecto para el destino del stock: la `principal`, o si no hay
 * ninguna, la primera de la lista (tal y como llega el backend, por `created_at`).
 * Devuelve `null` si no hay ubicaciones.
 */
export function ubicacionPorDefecto(locations) {
  if (!Array.isArray(locations) || locations.length === 0) return null;
  return locations.find((l) => l.kind === 'principal') ?? locations[0];
}

/**
 * ¿Puede SALIR una venta de esta ubicación con este stock? Espejo de
 * `decide_sale_location` del backend (ficha stock-almacen § regla de oro):
 * ubicación de sala = no-principal Y dispensable (semáforo 🟢), y con stock
 * positivo. Una en negativo no es fuente (Bug 2: el add se ancla, el backend la
 * rechaza y el carrito se cuelga). Una en 🔴 NO EXISTE para las ventas: no sale
 * en «¿De dónde sale?» aunque tenga stock — es el almacén externo/frío que está
 * físicamente fuera del club (incidente EXTERNO de Kong, 09-08-2026).
 *
 * `dispensable` ausente = true (default del backend; nadie nota nada sin tocarlo).
 * Vale para las dos formas que llegan del backend: el board (`{kind, dispensable}`
 * + cantidad aparte) y `by-location` (`{kind, dispensable, quantity}`).
 *
 * Un dato, un render: Barra y Dispensario la usan; el Dispensario tenía su copia
 * sin el `dispensable` y ofrecía la roja en el popup (Kong House Test, 11-09-2026).
 */
export function esFuenteDeVenta(loc, quantity = loc?.quantity) {
  if (!loc || loc.kind === 'principal') return false;
  if (loc.dispensable === false) return false;
  return (Number(quantity) || 0) > 0;
}

/**
 * De dónde viene y a dónde va un movimiento del historial de stock (petición de
 * cliente, 21-10-2026). Un dato, un render: lo usan el Historial de «Actualizar
 * stock» y el detalle de Actividad.
 *
 * La entrada viene de fuera del club: su origen es el proveedor de la factura o,
 * sin factura, «Entrada externa». Si al movimiento le falta un lado (anteriores a
 * que el ledger lo guardara), ese lado se omite — no se inventa la ubicación.
 * Devuelve '' si no hay nada que decir.
 */
export function describirUbicacionMovimiento(m) {
  if (!m) return '';
  const desde = m.from_location_name || '';
  const hacia = m.to_location_name || '';
  switch (m.movement_type) {
    case 'entrada': {
      const origen = m.supplier_name || 'Entrada externa';
      if (hacia) return `${origen} → ${hacia}`;
      return m.supplier_name ? `Desde ${m.supplier_name}` : origen;
    }
    case 'traspaso':
      if (desde && hacia) return `${desde} → ${hacia}`;
      if (desde) return `Desde ${desde}`;
      return hacia ? `Hacia ${hacia}` : '';
    case 'salida':
    case 'venta':
      return desde ? `${desde} →` : '';
    case 'devolucion':
      return hacia ? `→ ${hacia}` : '';
    case 'ajuste':
      return hacia || desde ? `en ${hacia || desde}` : '';
    default:
      if (desde && hacia) return `${desde} → ${hacia}`;
      return desde || hacia;
  }
}
